/* eslint-disable no-lonely-if */
import { Edge, Node } from '@xyflow/react';
import { IVlanItem } from 'src/context/global/types';
import {
  EUntaggedVlan,
  IEdgeExtraData,
  EAllowIncomingPackets,
} from 'src/modules/network-configuration/edges/types';
import {
  EDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import {
  IConfigurationVlan,
  IConfigurationMember,
  IConfigurationBridge,
  IConfigurationVlanVid,
  IConfigurationVlanInterface,
} from 'src/types/configuration';

import { isV4Format, calculateNetworkAddress2 } from '../ip';
import {
  getEdgeConnected,
  getInfoConnected,
  getAllNodeConnected,
  getPortIndexConnected,
} from '../node';

export const configurationVlan = ({
  nodeId,
  nodeCategory,
  edges,
  nodes,
  vlans,
  isAutoDeploy,
  autoDeployIps,
}: {
  nodeId: string;
  nodeCategory: NodeCategoryModel;
  edges: Edge[];
  nodes: Node[];
  vlans: IVlanItem[];
  isAutoDeploy: boolean;
  autoDeployIps?: {
    id: string;
    ip: string;
  }[];
}): IConfigurationVlan => {
  const hasVlan =
    nodeCategory.ipInterfaces && nodeCategory.ipInterfaces.length > 0;

  const gateway = nodeCategory.gateway || '';
  const isValidGateway =
    isV4Format(gateway) && gateway.replace(/\./g, '').length > 0;

  if (!hasVlan) {
    const vlanWithoutVlan: IConfigurationVlan = {
      bridges: [
        {
          forward: false,
          gw: isValidGateway ? gateway : '',
          ip: nodeCategory.ip || '',
          proxy_arp: false,
          sm: nodeCategory.subnetMask || '',
          vid: 0,
          wan: false,
        },
      ],
      global: {
        enabled: hasVlan || false,
      },
      version: 3,
    };

    return vlanWithoutVlan;
  }

  const vlan: IConfigurationVlan = {
    bridges: [],
    global: {
      enabled: hasVlan || false,
    },
    interfaces: [],
    version: 3,
    vids: [],
  };

  nodeCategory.ipInterfaces?.forEach((ipInterface) => {
    const bridgeGw =
      isValidGateway &&
      ipInterface.ip &&
      ipInterface.subnetMask &&
      calculateNetworkAddress2(gateway, ipInterface.subnetMask) ===
        calculateNetworkAddress2(ipInterface.ip, ipInterface.subnetMask)
        ? gateway
        : '';
    vlan.bridges.push({
      forward: false,
      gw: bridgeGw,
      ip: ipInterface.ip || '',
      proxy_arp: false,
      sm:
        ipInterface.ip && ipInterface.subnetMask ? ipInterface.subnetMask : '',
      vid: parseInt(ipInterface.vlanId.toString(), 10),
      wan: false,
    });
  });

  const allInterfaces = nodeCategory.ipInterfaces?.sort(
    (a, b) =>
      parseInt(a.vlanId.toString(), 10) - parseInt(b.vlanId.toString(), 10),
  );

  const lowestVlanId = allInterfaces?.[0]?.vlanId ?? 1;

  const interfaceItem: IConfigurationVlanInterface[] = [];
  if (nodeCategory.ports) {
    for (let i = 0; i < nodeCategory.ports?.length; i += 1) {
      const port = nodeCategory.ports[i];
      const { portIndexes } = nodeCategory;
      const portIndex = portIndexes[port.id] || 0;
      if (!portIndex) {
        throw new Error('Port index not found');
      }

      const { edge } = getInfoConnected(nodeId, port.id, edges, nodes);
      const allVlanConnected = ((edge?.data?.vlanIds as number[]) ?? []).sort(
        (a, b) => a - b,
      );
      const lowestVlanIdConnected =
        allVlanConnected?.length > 0
          ? parseInt(allVlanConnected[0].toString(), 10)
          : lowestVlanId;

      const edgeData = edge?.data as IEdgeExtraData;
      const untaggedVlan = edgeData?.untaggedVlan || EUntaggedVlan.AUTO;
      // handle tags
      let tags = parseInt(lowestVlanIdConnected.toString(), 10);
      // skip if else to handle to feature
      if (untaggedVlan === EUntaggedVlan.AUTO) {
        // default
      } else if (untaggedVlan === EUntaggedVlan.NONE) {
        // default
      } else if (untaggedVlan) {
        const untaggedVlanValue = parseInt(untaggedVlan, 10);
        if (allVlanConnected.includes(untaggedVlanValue)) {
          tags = untaggedVlanValue;
        }
      }

      // handle discard
      let isDiscardTagged = false;
      let isDiscardUntagged = false;

      if (edge) {
        const isMultipleVlan = allVlanConnected.length > 1;
        const allowIncomingPackets =
          edgeData?.allowIncomingPackets || EAllowIncomingPackets.AUTO;

        const portHasUntaggedVlan = (
          isMultiple: boolean,
          untagged: string,
        ): boolean => {
          if (untagged === EUntaggedVlan.NONE) {
            return false;
          }
          // Multiple VLANs + AUTO means all egress tagged (no untagged VLAN).
          if (isMultiple && untagged === EUntaggedVlan.AUTO) {
            return false;
          }
          return true;
        };

        // Helper function to determine discard settings based on flowchart logic
        const getDiscardSettings = (
          isMultiple: boolean,
          untagged: string,
          allowPackets: EAllowIncomingPackets,
          isAuto: boolean,
        ) => {
          const settings = { tagged: false, untagged: false };

          if (
            allowPackets === EAllowIncomingPackets.ALLOW_TAGGED_AND_UNTAGGED
          ) {
            return settings;
          }

          if (allowPackets === EAllowIncomingPackets.ALLOW_TAGGED) {
            settings.untagged = true;
            return settings;
          }

          const hasUntagged = portHasUntaggedVlan(isMultiple, untagged);

          if (!hasUntagged) {
            // No untagged VLAN: drop untagged for AUTO/tagged, drop tagged for untagged
            if (allowPackets === EAllowIncomingPackets.ALLOW_UNTAGGED) {
              settings.tagged = true;
            } else {
              settings.untagged = true;
            }
            return settings;
          }

          // Has untagged VLAN on this port
          if (allowPackets === EAllowIncomingPackets.ALLOW_UNTAGGED) {
            if (isMultiple || !isAuto) {
              settings.tagged = true;
            }
            return settings;
          }

          // AUTO with untagged VLAN configured
          if (isMultiple) {
            return settings;
          }

          if (!isAuto) {
            settings.tagged = true;
          }

          return settings;
        };

        const discardSettings = getDiscardSettings(
          isMultipleVlan,
          untaggedVlan,
          allowIncomingPackets,
          isAutoDeploy,
        );

        isDiscardTagged = discardSettings.tagged;
        isDiscardUntagged = discardSettings.untagged;
      }

      const interfaceItemData = {
        discardtagged: isAutoDeploy ? false : isDiscardTagged,
        discarduntagged: isAutoDeploy ? false : isDiscardUntagged,
        forcedefaultvid: false,
        interface: portIndex,
        tags,
      };

      interfaceItem.push(interfaceItemData);
    }

    vlan.interfaces = interfaceItem.sort((a, b) => a.interface - b.interface);
  }
  const vlanVids = vlans.map((vlanItem) => {
    const members: IConfigurationMember[] = [];
    const edgeHasVlan = edges.filter((edge) =>
      (edge.data?.vlanIds as number[])
        ?.map((vlanId) => parseInt(vlanId.toString(), 10))
        ?.includes(parseInt(vlanItem.vlanId.toString(), 10)),
    );

    edgeHasVlan.forEach((edge) => {
      const portIndex = getPortIndexConnected(nodeId, edge, nodeCategory);
      let egress: 'tagged' | 'untagged' = 'untagged';
      const edgeData = edge.data as IEdgeExtraData;
      const untaggedVlan = edgeData?.untaggedVlan || EUntaggedVlan.AUTO;
      if (untaggedVlan === EUntaggedVlan.AUTO) {
        egress =
          edgeData.vlanIds?.length && edgeData.vlanIds.length > 1
            ? 'tagged'
            : 'untagged';
      } else if (untaggedVlan === EUntaggedVlan.NONE) {
        egress = 'tagged';
      } else if (untaggedVlan.toString() === vlanItem.vlanId.toString()) {
        egress = 'untagged';
      } else {
        egress = 'tagged';
      }

      if (portIndex) {
        members.push({ egress, interface: portIndex });
      }
    });

    return {
      members: members.sort((a, b) => a.interface - b.interface),
      name: vlanItem.description,
      vid: parseInt(vlanItem.vlanId.toString(), 10),
    };
  });

  vlan.vids = vlanVids;

  if (isAutoDeploy) {
    const autoDeployBridges: IConfigurationBridge[] =
      autoDeployIps
        ?.filter((ip) => ip.id === nodeId)
        .map((ip) => ({
          forward: false,
          gw: '',
          ip: ip.ip,
          proxy_arp: false,
          sm: '255.255.255.0',
          vid: 3579,
          wan: false,
        })) ?? [];

    vlan.bridges.push(...autoDeployBridges);

    const vidAutoDeploy: IConfigurationVlanVid = {
      members: [],
      name: 'Auto Configuration Deployment',
      vid: 3579,
    };

    // add vlan auto deploy for all switch connected
    const allNodeConnected = getAllNodeConnected(nodeId, edges, nodes);
    const allSwitchConnected = allNodeConnected.filter(
      (node) =>
        (node.data?.category as NodeCategoryModel).type === EDataType.Switch,
    );

    allSwitchConnected.forEach((switchNode) => {
      const edgeConnected = getEdgeConnected(nodeId, switchNode.id, edges);
      if (edgeConnected) {
        const portIndex = getPortIndexConnected(
          nodeId,
          edgeConnected,
          nodeCategory,
        );
        if (portIndex) {
          vidAutoDeploy.members.push({
            egress: 'tagged',
            interface: portIndex,
          });
        }
      }
    });

    vlan.vids.push(vidAutoDeploy);
  }

  return vlan;
};
